<template>
  <div
    class="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50/10"
  >
    <div class="w-full max-w-md relative z-10">
      <!-- Loading State -->
      <transition
        name="fade-slide"
        appear
      >
        <div
          v-if="loading"
          class="bg-white/85 backdrop-blur-xl rounded-3xl p-10 text-center border border-white/30 transform scale-100"
        >
          <div class="flex flex-col items-center space-y-8">
            <!-- Animated Loading Ring -->
            <div class="relative">
              <div
                class="w-24 h-24 border-4 border-blue-100 rounded-full animate-spin-slow shadow-inner"
              >
                <div
                  class="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"
                ></div>
              </div>
              <div class="absolute inset-0 flex items-center justify-center">
                <UserCheck class="w-10 h-10 text-blue-600 animate-pulse" />
              </div>
              <!-- Ripple Effect -->
              <div
                class="absolute inset-0 rounded-full border-2 border-blue-300 animate-ping opacity-20"
              ></div>
            </div>
            <div class="space-y-4">
              <h2
                class="text-3xl font-extrabold text-gray-800 animate-fade-in-up"
              >
                Processing Attendance
              </h2>
              <div class="flex items-center justify-center space-x-1.5">
                <span class="text-gray-600 text-lg">Please wait</span>
                <div class="flex space-x-1.5">
                  <div
                    class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                    style="animation-delay: 0ms"
                  ></div>
                  <div
                    class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                    style="animation-delay: 150ms"
                  ></div>
                  <div
                    class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"
                    style="animation-delay: 300ms"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Success State -->
      <transition
        name="success-bounce"
        appear
      >
        <div
          v-if="success"
          class="bg-white/85 backdrop-blur-xl rounded-3xl p-10 text-center border border-white/30 relative overflow-hidden"
        >
          <!-- Confetti Animation -->
          <div class="absolute inset-0 pointer-events-none">
            <div
              v-for="i in 30"
              :key="i"
              class="absolute w-2 h-2 rounded-full animate-confetti"
              :style="{
                left: Math.random() * 100 + '%',
                backgroundColor: ['#81C784', '#66BB6A', '#4CAF50', '#388E3C'][
                  Math.floor(Math.random() * 4)
                ],
                animationDelay: Math.random() * 2 + 's',
                animationDuration: 2 + Math.random() * 2 + 's',
                transform: `translateY(-100vh) rotate(${
                  Math.random() * 360
                }deg)`,
              }"
            ></div>
          </div>
          <div class="flex flex-col items-center space-y-8 relative z-10">
            <!-- Success Icon with Pulse -->
            <div class="relative">
              <div
                class="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-scale-in shadow-lg"
              >
                <CheckCircle class="w-12 h-12 text-white animate-check-draw" />
              </div>
              <div
                class="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-25"
              ></div>
              <div
                class="absolute inset-0 bg-green-400 rounded-full animate-pulse opacity-20"
              ></div>
            </div>
            <div class="space-y-5">
              <h2
                class="text-[16px] sm:text-3xl font-extrabold text-gray-800 animate-fade-in-up"
              >
                Attendance Confirmed!
              </h2>

              <p
                class="text-[10px] mt-[11px] sm:text-lg sm:mt-0 text-gray-600 animate-fade-in-up animation-delay-200"
              >
                Successfully marked attendance for
              </p>

              <div
                class="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 animate-fade-in-up animation-delay-400 transform hover:scale-105 transition-transform duration-200 shadow-md"
              >
                <p class="font-bold text-green-800 text-xl">
                  {{ studentName }}
                </p>
              </div>
            </div>
            <div
              class="text-[10px] sm:text-base flex items-center space-x-2 text-gray-500 animate-fade-in-up animation-delay-600"
            >
              <Clock class="w-5 h-5 animate-tick" />
              <span>{{ currentTime }}</span>
            </div>
          </div>
        </div>
      </transition>

      <!-- Error State -->
      <transition
        name="shake"
        appear
      >
        <div
          v-if="error"
          class="bg-white/85 backdrop-blur-xl rounded-3xl p-10 text-center border border-white/30"
        >
          <div class="flex flex-col items-center space-y-8">
            <!-- Error Icon with Shake -->
            <div class="relative">
              <div
                class="w-24 h-24 bg-gradient-to-r from-red-400 to-rose-500 rounded-full flex items-center justify-center animate-scale-in shadow-lg"
              >
                <AlertCircle class="w-12 h-12 text-white animate-shake" />
              </div>
              <div
                class="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-25"
              ></div>
            </div>
            <div class="space-y-5">
              <h2
                class="text-[20px] sm:text-3xl font-extrabold text-gray-800 animate-fade-in-up"
              >
                Attendance Failed
              </h2>

              <p
                class="text-[9px] mt-[2px] sm:text-lg sm:mt-0 text-gray-600 animate-fade-in-up animation-delay-200"
              >
                We encountered an issue while marking attendance
              </p>

              <div
                class="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-5 animate-fade-in-up animation-delay-400 shadow-md"
              >
                <p class="text-[12px] sm:text-base text-red-800">
                  {{ error }}
                </p>
              </div>
            </div>
            <button
              @click="retryAttendance"
              class="inline-flex items-center space-x-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl animate-fade-in-up animation-delay-600 active:scale-95 group"
            >
              <RefreshCw
                class="w-5 h-5 transition-transform duration-300 group-hover:rotate-180"
              />
              <span class="text-[10px] sm:text-base">Try Again</span>
            </button>
          </div>
        </div>
      </transition>

      <!-- Footer -->
      <transition
        name="fade-up"
        appear
      >
        <div class="mt-10 text-center animate-fade-in-up animation-delay-800">
          <div
            class="inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/40 shadow-sm"
          >
            <div
              class="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse"
            ></div>
            <p class="text-[10px] sm:text-base text-gray-700 font-medium">
              Powered by NFC Technology
            </p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  import { onMounted, ref, computed } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useStore } from "vuex";
  import {
    UserCheck,
    CheckCircle,
    AlertCircle,
    Clock,
    RefreshCw,
  } from "lucide-vue-next";
  import axios from "axios";
  const BASE_URL = process.env.VUE_APP_BASE_URL;
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const studentId = route.params.studentId;
  const loading = ref(true);
  const success = ref(false);
  const error = ref("");
  const studentName = ref("Student");
  const currentTime = computed(() => {
    return new Date().toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  });
  const markAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      loading.value = true;
      success.value = false;
      error.value = "";
      // ✅ Get student name
      const res = await axios.get(`${BASE_URL}/api/students/${studentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      studentName.value = res.data.name;
      // ✅ Dispatch markAttendance action from store
      await store.dispatch("markAttendance", {
        studentId,
        status: "present",
        subject: "",
        notes: "Marked via NFC link",
        attendanceByNFC: false,
      });
      success.value = true;

      setTimeout(() => {
        router.replace("/");
      }, 5000);
    } catch (err) {
      error.value =
        err.response?.data?.error ||
        "Failed to mark attendance. Please try again.";
      console.error("MarkAttendance Error:", err);
    } finally {
      loading.value = false;
    }
  };
  const retryAttendance = () => {
    markAttendance();
  };
  onMounted(() => {
    markAttendance();
  });
</script>

<style scoped>
  /* Blob Animation */
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(40px, -60px) scale(1.15);
    }
    66% {
      transform: translate(-30px, 30px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }
  .animate-blob {
    animation: blob 8s infinite cubic-bezier(0.42, 0, 0.58, 1);
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }

  /* Custom Animations */
  @keyframes spin-slow {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes scale-in {
    0% {
      transform: scale(0) rotate(180deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.1) rotate(90deg);
      opacity: 0.8;
    }
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
  }
  @keyframes fade-in-up {
    0% {
      transform: translateY(20px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
  @keyframes check-draw {
    0% {
      stroke-dasharray: 0 100;
    }
    100% {
      stroke-dasharray: 100 0;
    }
  }
  @keyframes confetti {
    0% {
      transform: translateY(-100vh) rotate(0deg) scale(0.5);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg) scale(1.2);
      opacity: 0;
    }
  }
  @keyframes tick {
    0%,
    50% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-10deg);
    }
    75% {
      transform: rotate(10deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-8px);
    }
    75% {
      transform: translateX(8px);
    }
  }

  /* Animation Classes */
  .animate-spin-slow {
    animation: spin-slow 3s linear infinite;
  }
  .animate-scale-in {
    animation: scale-in 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }
  .animate-check-draw {
    animation: check-draw 0.5s ease-in-out 0.3s both;
  }
  .animate-confetti {
    animation: confetti linear;
  }
  .animate-tick {
    animation: tick 2s ease-in-out infinite;
  }
  .animate-shake {
    animation: shake 0.5s ease-in-out;
  }

  /* Animation Delays */
  .animation-delay-200 {
    animation-delay: 0.2s;
  }
  .animation-delay-400 {
    animation-delay: 0.4s;
  }
  .animation-delay-600 {
    animation-delay: 0.6s;
  }
  .animation-delay-800 {
    animation-delay: 0.8s;
  }

  /* Vue Transitions */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  .success-bounce-enter-active {
    transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .success-bounce-enter-from {
    opacity: 0;
    transform: translateY(50px) scale(0.8);
  }
  .shake-enter-active {
    transition: all 0.5s ease-out;
  }
  .shake-enter-from {
    opacity: 0;
    transform: translateX(-20px) scale(0.9);
  }
  .fade-up-enter-active {
    transition: all 0.8s ease-out;
  }
  .fade-up-enter-from {
    opacity: 0;
    transform: translateY(20px);
  }

  /* Glassmorphism Effect */
  .bg-white\/85 {
    background-color: rgba(255, 255, 255, 0.85);
  }
  .bg-white\/70 {
    background-color: rgba(255, 255, 255, 0.7);
  }
  .border-white\/30 {
    border-color: rgba(255, 255, 255, 0.3);
  }
  .border-white\/40 {
    border-color: rgba(255, 255, 255, 0.4);
  }
</style>
